import React, { useEffect, useState } from 'react';
import {
  Button,
  Chip,
  Grid,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import YouTubeIcon from '@mui/icons-material/YouTube';
import DashboardLayout from '../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { getResources, deleteResource } from '../services/resourceService';
import { resolveAssetUrl } from '../utils/assetUrl';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function ResourcesPage() {
  const [resources, setResources] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedResources,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(resources, 10);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const data = await getResources();
      setResources(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) {
      return;
    }

    try {
      await deleteResource(id);
      fetchResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <AdminPageHeader
          eyebrow="Content"
          title="Resources"
          description="Curate articles, guides, templates, and media resources with a consistent presentation."
          meta={<Chip label={`${resources.length} resources`} variant="outlined" />}
          action={
            <Button variant="contained" onClick={() => navigate('/resources/create')}>
              Add Resource
            </Button>
          }
        />

        {isMobile ? (
          resources.length ? (
            <Grid container spacing={2}>
              {paginatedResources.map((resource) => (
                <Grid item xs={12} key={resource.resourceId}>
                  <AdminSection>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Stack spacing={0.5}>
                          <Typography variant="h6">{resource.title}</Typography>
                          <Chip label={resource.type || 'Resource'} size="small" variant="outlined" sx={{ width: 'fit-content' }} />
                        </Stack>
                        {resource.imageSrc ? (
                          <img
                            src={resolveAssetUrl(resource.imageSrc)}
                            alt={resource.title}
                            style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, border: '1px solid #E2E8F0' }}
                          />
                        ) : null}
                      </Stack>
                      <Typography color="text.secondary">
                        {String(resource.content || '').replace(/<[^>]*>/g, '').slice(0, 160) || 'No content'}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                        {resource.videoUrl && resource.type === 'Media' ? (
                          <Link href={resource.videoUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <YouTubeIcon fontSize="small" />
                            Open video
                          </Link>
                        ) : <span />}
                        <Stack direction="row" spacing={1}>
                          <Button variant="outlined" onClick={() => navigate(`/resources/edit/${resource.resourceId}`)}>
                            Edit
                          </Button>
                          <IconButton color="error" onClick={() => handleDelete(resource.resourceId)}>
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Stack>
                  </AdminSection>
                </Grid>
              ))}
            </Grid>
          ) : (
            <AdminSection>
              <AdminEmptyState
                title="No resources yet"
                description="Create the first resource to populate the knowledge library."
              />
            </AdminSection>
          )
        ) : (
          <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Video</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resources.length ? (
                  paginatedResources.map((resource) => (
                    <TableRow key={resource.resourceId}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography sx={{ fontWeight: 600 }}>{resource.title}</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 320 }}>
                            {String(resource.content || '').replace(/<[^>]*>/g, '') || '-'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={resource.type || 'Resource'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {resource.imageSrc ? (
                          <img
                            src={resolveAssetUrl(resource.imageSrc)}
                            alt={resource.title}
                            width="44"
                            height="44"
                            style={{ objectFit: 'cover', borderRadius: 10, border: '1px solid #E2E8F0' }}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {resource.videoUrl && resource.type === 'Media' ? (
                          <Link href={resource.videoUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <YouTubeIcon fontSize="small" />
                            Open
                          </Link>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/resources/edit/${resource.resourceId}`)}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(resource.resourceId)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <AdminEmptyState
                        title="No resources yet"
                        description="Create the first resource to populate the knowledge library."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </AdminSection>
        )}
      </Stack>
    </DashboardLayout>
  );
}

export default ResourcesPage;
